import { ReCaptchaSetting } from 'backend';
import { ROOT_ELEMENT_ID } from 'models';
import {
  getReCaptchaScriptSrc,
  getReCaptchaBadgeElement,
  RE_CAPTCHA_SCRIPT_ID,
  getReCaptchaScriptElement,
} from 'models';
import { useEffect } from 'react';
import {
  setReCaptchaReady,
  useReCaptchaReady,
  useSetReCaptchaReadyDispatch,
} from 'store';
import { EXECUTE_RE_CAPTCHA_RESULT_TYPE } from './models';

const _window: any = window;

export type UseReCaptchaPayload = ReCaptchaSetting;

const loadReCaptcha = (
  siteKey: ReCaptchaSetting['siteKey'],
  onLoad: () => void
) => {
  const script = document.createElement('script');
  script.id = RE_CAPTCHA_SCRIPT_ID;
  script.src = getReCaptchaScriptSrc(siteKey);
  script.async = true;
  script.defer = true;
  script.onload = onLoad;
  const rootEl = document.getElementById(ROOT_ELEMENT_ID);
  if (!rootEl) {
    throw new Error('ROOT_ELEMENT is null!!');
  }
  rootEl.parentNode?.insertBefore(script, rootEl.nextSibling);
};

type ExecuteReCaptchaResultNotUse = {
  type: typeof EXECUTE_RE_CAPTCHA_RESULT_TYPE.notUsed;
};

type ExecuteReCaptchaResultSuccess = {
  type: typeof EXECUTE_RE_CAPTCHA_RESULT_TYPE.success;
  token: string;
};

type ExecuteReCaptchaResultError = {
  type: typeof EXECUTE_RE_CAPTCHA_RESULT_TYPE.error;
  token: '';
};

export type ExecuteReCaptchaResult =
  | ExecuteReCaptchaResultNotUse
  | ExecuteReCaptchaResultSuccess
  | ExecuteReCaptchaResultError;

export type UseReCaptchaReturnType = {
  executeReCaptcha: () => Promise<ExecuteReCaptchaResult>;
};

export const useReCaptcha = (
  payload?: UseReCaptchaPayload
): UseReCaptchaReturnType => {
  const isReady = useReCaptchaReady();
  const dispatch = useSetReCaptchaReadyDispatch();
  const dispatchReCaptchaReady = (ready: boolean) => {
    dispatch(
      setReCaptchaReady({
        ready,
      })
    );
  };
  const removeReCaptchaIfNeeded = () => {
    const badge = getReCaptchaBadgeElement();
    const script = getReCaptchaScriptElement();
    if (badge && badge.parentNode) {
      document.body.removeChild(badge.parentNode);
    }
    if (script) {
      script.parentNode?.removeChild(script);
    }
    dispatchReCaptchaReady(false);
  };

  const loadReCaptchaIFNeeded = () => {
    if (!payload) {
      return;
    }
    if (isReady) {
      return;
    }
    const handleReCaptchaLoaded = () => {
      _window.grecaptcha.ready(() => {
        dispatchReCaptchaReady(true);
      });
    };
    loadReCaptcha(payload.siteKey, handleReCaptchaLoaded);
  };

  const executeReCaptcha: UseReCaptchaReturnType['executeReCaptcha'] =
    async () => {
      if (!payload) {
        return {
          type: EXECUTE_RE_CAPTCHA_RESULT_TYPE.notUsed,
        };
      }
      const { siteKey, action } = payload;
      try {
        const token = await _window.grecaptcha.execute(siteKey, { action });
        return {
          type: EXECUTE_RE_CAPTCHA_RESULT_TYPE.success,
          token: token ?? '',
        };
      } catch (_e) {
        return {
          type: EXECUTE_RE_CAPTCHA_RESULT_TYPE.error,
          token: '',
        };
      }
    };
  useEffect(() => {
    if (payload) {
      loadReCaptchaIFNeeded();
    } else {
      removeReCaptchaIfNeeded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  return {
    executeReCaptcha,
  };
};
